import { useState, useRef, useCallback } from "react";
import {
  X,
  Upload,
  FileText,
  Star,
  AlertCircle,
  CheckCircle2,
  ChevronDown,
  ChevronUp,
  Trophy,
  MapPin,
} from "lucide-react";

const API_BASE =
  (process.env.REACT_APP_API_URL &&
    process.env.REACT_APP_API_URL.replace(/\/$/, "")) ||
  "";

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function compositeBadge(score) {
  if (score == null) return { label: "No score", cls: "pc-pill-neutral" };
  if (score >= 80) return { label: "Excellent", cls: "pc-pill-emerald" };
  if (score >= 60) return { label: "Strong", cls: "pc-pill-lime" };
  if (score >= 40) return { label: "Average", cls: "pc-pill-amber" };
  if (score >= 20) return { label: "Below Avg", cls: "pc-pill-orange" };
  return { label: "High Risk", cls: "pc-pill-rose" };
}

function qualityBadge(score) {
  if (score == null) return { label: "Unscored", cls: "pc-pill-neutral" };
  if (score >= 75) return { label: "Top Tier", cls: "pc-pill-emerald" };
  if (score >= 55) return { label: "Good", cls: "pc-pill-lime" };
  if (score >= 35) return { label: "Average", cls: "pc-pill-amber" };
  return { label: "Below Avg", cls: "pc-pill-orange" };
}

function fmt(val, digits = 1) {
  if (val === null || val === undefined || isNaN(val)) return "—";
  return Number(val).toFixed(digits);
}

function Stars({ rating }) {
  if (!rating) return <span className="pc-muted">—</span>;
  const full = Math.round(rating);
  return (
    <span className="pc-stars">
      {Array.from({ length: 5 }, (_, i) => (
        <Star
          key={i}
          size={11}
          className={i < full ? "pc-star-filled" : "pc-star-empty"}
        />
      ))}
      <span className="pc-star-label">{fmt(rating)}</span>
    </span>
  );
}

// ---------------------------------------------------------------------------
// Generate a plain-English explanation of why a facility ranked where it did
// ---------------------------------------------------------------------------

function generateRankNote(item, totalRanked) {
  const composite = item.Composite_Score ?? item.composite_score ?? null;
  const rating = item.overall_rating ?? null;
  const hist = item.latest_metrics || {};
  const discharge = hist.discharge_to_community_rate ?? null;
  const readmission = hist.preventable_readmission_rate ?? null;
  const falls = hist.fall_with_major_injury_rate ?? null;

  const drivers = [];

  // Primary driver: composite score
  if (composite != null) {
    if (composite >= 75)
      drivers.push(`high composite quality score (${Math.round(composite)}/100)`);
    else if (composite >= 55)
      drivers.push(`above-average composite score (${Math.round(composite)}/100)`);
    else if (composite >= 35)
      drivers.push(`average composite score (${Math.round(composite)}/100)`);
    else
      drivers.push(`below-average composite score (${Math.round(composite)}/100)`);
  } else if (rating != null) {
    drivers.push(`${fmt(rating, 1)}-star CMS rating`);
  }

  // Secondary driver: standout outcome metric
  if (discharge != null) {
    if (discharge >= 78) drivers.push(`strong discharge-to-home rate (${fmt(discharge, 0)}%)`);
    else if (discharge < 55) drivers.push(`low discharge-to-home rate (${fmt(discharge, 0)}%)`);
  }
  if (readmission != null && drivers.length < 2) {
    if (readmission < 10) drivers.push(`low readmission rate (${fmt(readmission, 1)}%)`);
    else if (readmission > 20) drivers.push(`high readmission rate (${fmt(readmission, 1)}%)`);
  }
  if (falls != null && drivers.length < 2) {
    if (falls < 0.4) drivers.push("minimal fall-injury incidents");
    else if (falls > 1.2) drivers.push(`elevated fall-injury rate (${fmt(falls, 1)}%)`);
  }

  if (drivers.length === 0) {
    return item.rank <= 3
      ? "Ranks among the top facilities on this form based on available CMS data."
      : "Ranked using available CMS quality data.";
  }

  const lead =
    item.rank === 1
      ? `Top-ranked of ${totalRanked} facilities`
      : item.rank <= 3
      ? `#${item.rank} of ${totalRanked}`
      : item.rank > totalRanked - 2
      ? `Ranked near the bottom (#${item.rank} of ${totalRanked})`
      : `Ranked #${item.rank} of ${totalRanked}`;

  return `${lead} — driven by ${drivers.slice(0, 2).join(" and ")}.`;
}

// ---------------------------------------------------------------------------
// Ranked list row — full width, print-friendly
// ---------------------------------------------------------------------------

const MEDAL = { 1: "pc-row-gold", 2: "pc-row-silver", 3: "pc-row-bronze" };

function RankComparison({ qualityRank, formPosition }) {
  if (!formPosition) {
    return (
      <div className="pc-list-rank">
        <div className="pc-rank-col">
          <span className="pc-rank-col-label">Quality</span>
          <span className="pc-rank-col-num">#{qualityRank}</span>
        </div>
      </div>
    );
  }

  const delta = formPosition - qualityRank; // positive = moved up vs form order
  let arrowCls = "pc-rank-arrow-same";
  let arrowSymbol = "=";
  let deltaLabel = null;
  if (delta > 0) {
    arrowCls = "pc-rank-arrow-up";
    arrowSymbol = "▲";
    deltaLabel = `+${delta}`;
  } else if (delta < 0) {
    arrowCls = "pc-rank-arrow-down";
    arrowSymbol = "▼";
    deltaLabel = `${delta}`;
  }

  return (
    <div className="pc-list-rank pc-list-rank-compare">
      <div className="pc-rank-col">
        <span className="pc-rank-col-label">Form</span>
        <span className="pc-rank-col-num pc-rank-form">#{formPosition}</span>
      </div>
      <div className={`pc-rank-arrow ${arrowCls}`} title={
        delta === 0
          ? "Same position on form and by quality"
          : delta > 0
          ? `Quality ranks this facility ${delta} place${delta !== 1 ? "s" : ""} higher than the form listed it`
          : `Quality ranks this facility ${Math.abs(delta)} place${Math.abs(delta) !== 1 ? "s" : ""} lower than the form listed it`
      }>
        <span className="pc-rank-arrow-sym">{arrowSymbol}</span>
        {deltaLabel && <span className="pc-rank-arrow-delta">{deltaLabel}</span>}
      </div>
      <div className="pc-rank-col">
        <span className="pc-rank-col-label">Quality</span>
        <span className="pc-rank-col-num pc-rank-quality">#{qualityRank}</span>
      </div>
    </div>
  );
}

function RankedRow({ item, totalRanked }) {
  const [expanded, setExpanded] = useState(false);

  const composite = item.Composite_Score ?? item.composite_score ?? null;
  const { label: compLabel, cls: compCls } = compositeBadge(composite);
  const { label: qualLabel, cls: qualCls } = qualityBadge(item.qualityScore);

  const hist = item.latest_metrics || {};
  const discharge = hist.discharge_to_community_rate ?? null;
  const readmission = hist.preventable_readmission_rate ?? null;
  const falls = hist.fall_with_major_injury_rate ?? null;
  const pressureUlcer = hist.pressure_ulcer_rate ?? null;

  const hasMetrics = discharge != null || readmission != null || falls != null || pressureUlcer != null;
  const hasDetail = item.historical_years_available > 0 || item.bed_count != null;

  const rankNote = generateRankNote(item, totalRanked);
  const rankCls = MEDAL[item.rank] || "pc-row-default";

  return (
    <div className={`pc-list-row ${rankCls}`}>
      {/* Rank comparison column */}
      <RankComparison qualityRank={item.rank} formPosition={item.formPosition} />

      {/* Main content */}
      <div className="pc-list-content">
        {/* Top line: name + badges */}
        <div className="pc-list-top">
          <div className="pc-list-name-block">
            <h3 className="pc-list-name">{item.facility_name}</h3>
            <p className="pc-list-location">
              <MapPin size={10} />
              {[item.city, item.state].filter(Boolean).join(", ")}
              {item.address ? ` · ${item.address}` : ""}
            </p>
          </div>
          <div className="pc-list-badges">
            <Stars rating={item.overall_rating} />
            <span className={`pc-pill ${qualCls}`}>{qualLabel}</span>
            {composite != null && (
              <span className={`pc-pill ${compCls}`}>
                {compLabel}&nbsp;({fmt(composite, 0)})
              </span>
            )}
          </div>
        </div>

        {/* Plain-English rank explanation */}
        <p className="pc-rank-note">{rankNote}</p>

        {/* Metrics bar */}
        {hasMetrics && (
          <div className="pc-list-metrics">
            {discharge != null && (
              <span className="pc-list-metric">
                <span className="pc-list-metric-label">Discharged Home</span>
                <span className="pc-list-metric-val good">{fmt(discharge)}%</span>
              </span>
            )}
            {readmission != null && (
              <span className="pc-list-metric">
                <span className="pc-list-metric-label">Readmission</span>
                <span className="pc-list-metric-val risk">{fmt(readmission)}%</span>
              </span>
            )}
            {falls != null && (
              <span className="pc-list-metric">
                <span className="pc-list-metric-label">Falls w/ Injury</span>
                <span className="pc-list-metric-val risk">{fmt(falls)}%</span>
              </span>
            )}
            {pressureUlcer != null && (
              <span className="pc-list-metric">
                <span className="pc-list-metric-label">Pressure Ulcers</span>
                <span className="pc-list-metric-val risk">{fmt(pressureUlcer)}%</span>
              </span>
            )}
          </div>
        )}

        {/* Expand toggle */}
        {hasDetail && (
          <button
            className="pc-expand-btn"
            onClick={() => setExpanded((v) => !v)}
          >
            {expanded
              ? <><ChevronUp size={12} /> Less</>
              : <><ChevronDown size={12} /> More detail</>}
          </button>
        )}

        {expanded && (
          <div className="pc-list-expanded">
            {item.bed_count != null && (
              <span className="pc-detail-chip">{item.bed_count} certified beds</span>
            )}
            {item.CCN && (
              <span className="pc-detail-chip">CCN {item.CCN}</span>
            )}
            {item.historical_years_available > 0 && (
              <span className="pc-detail-chip">
                {item.historical_years_available} yr historical data
              </span>
            )}
            {Object.entries(item.latest_metrics || {})
              .filter(([, v]) => v != null)
              .slice(0, 8)
              .map(([k, v]) => (
                <span key={k} className="pc-detail-chip">
                  {k.replace(/_/g, " ")}: {fmt(v)}%
                </span>
              ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Drop zone
// ---------------------------------------------------------------------------

function DropZone({ onFile, file }) {
  const inputRef = useRef(null);
  const [dragging, setDragging] = useState(false);

  const handleDrop = useCallback(
    (e) => {
      e.preventDefault();
      setDragging(false);
      const dropped = e.dataTransfer.files?.[0];
      if (dropped?.type === "application/pdf") onFile(dropped);
    },
    [onFile]
  );

  return (
    <div
      className={`pc-dropzone ${dragging ? "pc-dropzone-active" : ""} ${
        file ? "pc-dropzone-filled" : ""
      }`}
      onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
      onDragLeave={() => setDragging(false)}
      onDrop={handleDrop}
      onClick={() => inputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => e.key === "Enter" && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf"
        style={{ display: "none" }}
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) onFile(f);
          e.target.value = "";
        }}
      />
      {file ? (
        <>
          <FileText size={28} className="pc-drop-icon filled" />
          <p className="pc-drop-name">{file.name}</p>
          <p className="pc-drop-hint">Click or drag to replace</p>
        </>
      ) : (
        <>
          <Upload size={28} className="pc-drop-icon" />
          <p className="pc-drop-name">Drop patient choice form PDF here</p>
          <p className="pc-drop-hint">or click to browse · PDF only</p>
        </>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main panel
// ---------------------------------------------------------------------------

export default function PatientChoicePanel({ onClose, onRequestDemo }) {
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [results, setResults] = useState(null);

  async function handleRank() {
    if (!file) return;
    setLoading(true);
    setError(null);
    setResults(null);
    try {
      const form = new FormData();
      form.append("pdf", file);
      const resp = await fetch(`${API_BASE}/api/patient-choice/rank`, {
        method: "POST",
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) {
        setError(data.error || "An error occurred processing the form.");
        return;
      }
      setResults(data);
    } catch (err) {
      setError("Network error: " + err.message);
    } finally {
      setLoading(false);
    }
  }

  function handleReset() {
    setFile(null);
    setResults(null);
    setError(null);
  }

  return (
    <div className="pc-panel">
      {/* Header */}
      <div className="pc-panel-header">
        <div className="pc-panel-title-group">
          <p className="pc-panel-eyebrow">Patient Choice Ranker</p>
          <h2 className="pc-panel-title">
            {results
              ? `${results.matchedCount} SNFs ranked by quality`
              : "Upload a patient choice form"}
          </h2>
          {!results && (
            <p className="pc-panel-sub">
              Extract SNF names from any hospital's form and instantly rank them
              by CMS composite score, star rating, and outcomes data.
            </p>
          )}
        </div>
        <div className="pc-panel-header-actions">
          {results && (
            <button className="pc-reset-btn" onClick={handleReset}>
              <Upload size={12} /> New form
            </button>
          )}
          <button className="pc-close-btn" onClick={onClose}>
            <X size={14} /> Close
          </button>
        </div>
      </div>

      {/* Body */}
      <div className="pc-panel-body">
        {!results ? (
          <div className="pc-upload-area">
            <DropZone onFile={setFile} file={file} />
            {error && (
              <div className="pc-alert">
                <AlertCircle size={15} />
                <span>{error}</span>
              </div>
            )}
            <button
              className="pc-rank-btn"
              disabled={!file || loading}
              onClick={handleRank}
            >
              {loading ? (
                <><span className="pc-spinner" /> Analyzing form…</>
              ) : (
                <><Trophy size={15} /> Rank SNFs on this form</>
              )}
            </button>
            <p className="pc-hint-text">
              Works with Ballad Health, NYP, UPMC, Ascension, and custom hospital
              forms. The primary SNF choice table is automatically identified and ranked.
            </p>
          </div>
        ) : (
          <>
            {/* Stats bar */}
            <div className="pc-results-header">
              <div className="pc-results-stats">
                <div className="pc-stat">
                  <span className="pc-stat-num">{results.matchedCount}</span>
                  <span className="pc-stat-label">ranked</span>
                </div>
                <div className="pc-stat-divider" />
                <div className="pc-stat">
                  <span className="pc-stat-num">{results.extractedCount}</span>
                  <span className="pc-stat-label">on form</span>
                </div>
                {results.unmatched?.length > 0 && (
                  <>
                    <div className="pc-stat-divider" />
                    <div className="pc-stat">
                      <span className="pc-stat-num pc-stat-warn">
                        {results.unmatched.length}
                      </span>
                      <span className="pc-stat-label">unmatched</span>
                    </div>
                  </>
                )}
                <div className="pc-stat-divider" />
                <div className="pc-results-note">
                  <CheckCircle2 size={12} />
                  Ranked by CMS composite · star rating · discharge · readmission · safety
                </div>
              </div>
            </div>

            {/* Ranked list */}
            <div className="pc-ranked-list">
              {results.ranked.map((item) => (
                <RankedRow
                  key={item.CCN || item.extractedName}
                  item={item}
                  totalRanked={results.matchedCount}
                />
              ))}
            </div>

            {/* Unmatched */}
            {results.unmatched?.length > 0 && (
              <div className="pc-unmatched">
                <div className="pc-unmatched-header">
                  <AlertCircle size={13} />
                  <span>
                    {results.unmatched.length} name
                    {results.unmatched.length !== 1 ? "s" : ""} from the form
                    could not be matched in our database
                  </span>
                </div>
                <ul className="pc-unmatched-list">
                  {results.unmatched.map((name) => (
                    <li key={name}>{name}</li>
                  ))}
                </ul>
              </div>
            )}

            {/* Contact CTA */}
            <div className="pc-contact-cta">
              <div className="pc-contact-cta-text">
                <p className="pc-contact-cta-headline">Want a deeper review?</p>
                <p className="pc-contact-cta-body">
                  Small changes in how facility options are presented can significantly influence
                  patient decisions and affect readmission risk, care quality, and episode costs
                  under value-based contracts. Our team can provide a customized redesign
                  recommendation aligned with facility performance.
                </p>
              </div>
              <button
                className="pc-contact-cta-btn"
                onClick={onRequestDemo}
              >
                Request Consultation
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
